import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, QrCode, FileDown, BookOpen, X } from "lucide-react";
import api, { apiError, fileUrl } from "../lib/api";
import { BrandMark } from "../components/Layout";
import { useLang } from "../context/LanguageContext";

export default function PartsCatalog() {
  const nav = useNavigate();
  const { t } = useLang();
  const [parts, setParts] = useState([]);
  const [pn, setPn] = useState(""); const [name, setName] = useState("");
  const [qr, setQr] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/parts"); setParts(data); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!pn.trim()) return toast.error(t("Part number required"));
    try { await api.post("/parts", { part_number: pn.trim(), part_name: name.trim() || pn.trim() }); setPn(""); setName(""); toast.success(t("Part added")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/parts/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const importBook = async () => { try { const { data } = await api.post("/parts/import-from-book"); toast.success(`Imported${data?.count ? `: ${data.count}` : ""}`); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="min-h-screen bg-[#0A0A0A] max-w-md mx-auto sm:max-w-2xl md:max-w-4xl border-x border-zinc-800/40 pb-10">
      <div className="sticky top-0 z-40 bg-[#0A0A0A]/90 backdrop-blur-md px-4 py-4 border-b border-zinc-800/70 flex items-center justify-between">
        <button onClick={() => nav("/profile")} className="flex items-center gap-2 text-zinc-300"><ArrowLeft size={20} /> {t("Profile")}</button>
        <BrandMark />
      </div>
      <div className="px-4 pt-4">
        <h1 className="text-2xl font-extrabold text-white">{t("Parts Catalog")}</h1>
        <p className="text-zinc-400 text-sm">{t("Scannable QR codes for the mobile app.")}</p>

        <div className="flex flex-wrap gap-2 mt-4">
          <a href={fileUrl("parts/export.xlsx")} target="_blank" rel="noreferrer" className="bg-[#18181B] border border-zinc-800 text-zinc-200 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><FileDown size={16} /> {t("Export .xlsx")}</a>
          <button onClick={importBook} data-testid="catalog-import-btn" className="bg-[#18181B] border border-zinc-700 text-zinc-200 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><BookOpen size={16} /> {t("Import from Parts Book")}</button>
        </div>

        <div className="flex gap-2 mt-4">
          <input className="flex-1 bg-[#1c1c1f] border border-zinc-800 text-white rounded-xl px-3 py-2.5 focus:border-[#F26722] focus:outline-none" placeholder={t("Part number")} value={pn} onChange={(e) => setPn(e.target.value)} data-testid="catalog-pn-input" />
          <input className="flex-1 bg-[#1c1c1f] border border-zinc-800 text-white rounded-xl px-3 py-2.5 focus:border-[#F26722] focus:outline-none" placeholder={t("Name")} value={name} onChange={(e) => setName(e.target.value)} data-testid="catalog-name-input" />
          <button onClick={add} className="bg-[#F26722] text-white rounded-xl px-4" data-testid="catalog-add-btn"><Plus size={18} /></button>
        </div>

        <div className="mt-4 space-y-2">
          {parts.length === 0 && <p className="text-zinc-500 text-center py-8">{t("No parts yet. Add one or import from the parts book.")}</p>}
          {parts.map((p) => (
            <div key={p.id} className="flex items-center justify-between bg-[#18181B] border border-zinc-800 rounded-xl p-3.5">
              <div><p className="font-bold text-white">{p.part_name}</p><p className="text-xs text-[#F97316] font-mono">{p.part_number}</p></div>
              <div className="flex items-center gap-3">
                <button onClick={() => setQr(p)} className="text-[#F26722]" data-testid={`qr-${p.id}`}><QrCode size={20} /></button>
                <button onClick={() => del(p.id)} className="text-red-400"><Trash2 size={18} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {qr && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setQr(null)}>
          <div className="bg-[#121214] border border-zinc-800 rounded-2xl p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3"><h3 className="font-bold text-white">{qr.part_name}</h3><button onClick={() => setQr(null)} className="text-zinc-400"><X size={20} /></button></div>
            <div className="bg-white p-4 rounded-xl"><img src={fileUrl(`parts/${qr.id}/qr.png`)} alt="qr" className="w-56 h-56" data-testid="qr-image" /></div>
            <p className="text-xs text-[#F97316] font-mono mt-3">{qr.part_number}</p>
            <a href={fileUrl(`parts/${qr.id}/qr.png`)} target="_blank" rel="noreferrer" download className="inline-block mt-3 bg-[#F26722] text-white text-sm font-semibold px-4 py-2 rounded-lg">{t("Download QR")}</a>
          </div>
        </div>
      )}
    </div>
  );
}
